import { openDB } from 'idb'
import type { Brief } from './brief'
import type { Frame } from './frame'
import type { Judgment } from './judge'
import type { Routed, Sized } from './lab'
import type { Extracted } from './later'
import type { Candidate } from './lookup'
import type { Statement } from './statements'
import type { Described } from './anchor'

/**
 * v2's own derived data, in its own IndexedDB database so the app's database is never
 * migrated. Everything here is disposable and can be regenerated from notes.
 */
export const V2_DATABASE = 'minimany-contexts-v2'
export interface SavedBrief { contextId: string; key: string; promptVersion: string; generatedAt: string; callId: string; costMicrousd: number | null; brief: Brief }
/** Every stored result names the model that actually served it (OpenRouter may resolve an alias to a dated snapshot). */
export interface Call { callId: string; model: string; costMicrousd: number | null; elapsedMs: number }
export interface SavedFrame { contextId: string; key: string; /** The sources the frame was made from, in order: a frame is shown at once only while these are unchanged. */ sources?: string[]; /** Each item with the anchor that finds it again (anchor.ts), so ticks apply without the notes. Frames saved before it have none. */ anchors?: Described[]; generatedAt: string; pastSince: string | null; /** The option the owner had chosen when the frame was made (the frame is made again when it changes); absent for none. */ chosen?: string | null; judge: Call; judgment: Judgment; frame: Call & { value: Frame } }

/** One collections-lab packet's parsed answers, so a rerun never pays twice. */
export type LabRecord = { id: string; call: Call } & ({ kind: 'sizing'; rows: Sized[] } | { kind: 'router'; rows: Routed[] })

/** Per-note results for later lists, valid while the note's content hash and the version match. */
export interface NoteObject { noteId: string; hash: string; version: string; model: string; sized: Sized }
export interface NoteExtract { noteId: string; hash: string; version: string; model: string; items: Extracted[] }

/** Catalogue answers per lookup key (kept even when empty, so a title is asked once per version). */
export interface SavedLookup { key: string; version: string; at: string; candidates: Candidate[] }
/** The owner's own marks on later-list items, such as "watched". Kept only in v2's database. */
export interface Mark { key: string; watched: boolean; at: string }
/** A reading of a saved page or a new image (read.ts), keyed by what was read and the read version. */
export interface SavedRead { key: string; value: unknown; at: string }

/**
 * The owner's statements (statements.ts) are not derived data: they are what the owner said or did, so they live in a database
 * of their own, one per account, that nothing here ever clears. Account rules: docs/ARCHITECTURE.md.
 */
export const ownerDatabase = (accountId: string) => `${V2_DATABASE}-owner:${accountId}`
const ownerDb = (accountId: string) => openDB(ownerDatabase(accountId), 1, { upgrade(db) { db.createObjectStore('statements', { keyPath: 'id' }) } })

export async function loadStatements(accountId: string): Promise<Statement[]> {
  return (await ownerDb(accountId)).getAll('statements')
}

export async function storeStatements(accountId: string, rows: readonly Statement[]): Promise<void> {
  const db = await ownerDb(accountId)
  for (const row of rows) await db.put('statements', row)
}

const database = () => openDB(V2_DATABASE, 7, {
  upgrade(db) {
    if (!db.objectStoreNames.contains('briefs')) db.createObjectStore('briefs', { keyPath: 'contextId' })
    // Phase A's line-level sortings were retired; their cache is disposable.
    if (db.objectStoreNames.contains('sortings')) db.deleteObjectStore('sortings')
    if (!db.objectStoreNames.contains('frames')) db.createObjectStore('frames', { keyPath: 'contextId' })
    if (!db.objectStoreNames.contains('lab')) db.createObjectStore('lab', { keyPath: 'id' })
    if (!db.objectStoreNames.contains('objects')) db.createObjectStore('objects', { keyPath: 'noteId' })
    if (!db.objectStoreNames.contains('extracts')) db.createObjectStore('extracts', { keyPath: 'noteId' })
    if (!db.objectStoreNames.contains('lookups')) db.createObjectStore('lookups', { keyPath: 'key' })
    if (!db.objectStoreNames.contains('marks')) db.createObjectStore('marks', { keyPath: 'key' })
    if (!db.objectStoreNames.contains('reads')) db.createObjectStore('reads', { keyPath: 'key' })
  },
})

export async function readBrief(contextId: string): Promise<SavedBrief | undefined> {
  return (await database()).get('briefs', contextId)
}

export async function saveBrief(saved: SavedBrief): Promise<void> {
  await (await database()).put('briefs', saved)
}

export async function loadFrame(contextId: string): Promise<SavedFrame | undefined> {
  return (await database()).get('frames', contextId)
}

export async function storeFrame(saved: SavedFrame): Promise<void> {
  await (await database()).put('frames', saved)
}

export async function loadLab(id: string): Promise<LabRecord | undefined> {
  return (await database()).get('lab', id)
}

export async function storeLab(saved: LabRecord): Promise<void> {
  await (await database()).put('lab', saved)
}

export async function loadNoteResults(): Promise<{ objects: NoteObject[]; extracts: NoteExtract[] }> {
  const db = await database()
  return { objects: await db.getAll('objects'), extracts: await db.getAll('extracts') }
}

export async function loadLookupsAndMarks(): Promise<{ lookups: SavedLookup[]; marks: Mark[] }> {
  const db = await database()
  return { lookups: await db.getAll('lookups'), marks: await db.getAll('marks') }
}

export async function storeLookups(rows: SavedLookup[]): Promise<void> {
  const db = await database()
  for (const row of rows) await db.put('lookups', row)
}

export async function loadReads(keys: string[]): Promise<Map<string, unknown>> {
  const db = await database(), found = new Map<string, unknown>()
  for (const key of keys) { const row = await db.get('reads', key) as SavedRead | undefined; if (row) found.set(key, row.value) }
  return found
}

export async function storeReads(rows: SavedRead[]): Promise<void> {
  const db = await database()
  for (const row of rows) await db.put('reads', row)
}

export async function storeMark(mark: Mark): Promise<void> {
  await (await database()).put('marks', mark)
}

export async function storeNoteResults(store: 'objects' | 'extracts', rows: (NoteObject | NoteExtract)[]): Promise<void> {
  const db = await database()
  for (const row of rows) await db.put(store, row)
}
